/**
 * core/auth.ts · 用户认证 + 会话管理
 *
 * R14 严守 (Jack 永久规则):
 *   - PBKDF2 SHA-256 310k iterations (OWASP 2024 推荐)
 *   - 16 字节独立随机 salt (每账号唯一)
 *   - Web Crypto API (浏览器原生 · 0 依赖)
 *
 * 复用 v200.16.1 employees seed 40 账号 (4 锚点稳定):
 *   - GM01 / TN-GM-26 (总经理 朱杰 · admin)
 *   - SALES-01..10 / Trungnam2026! (sales)
 *   - FIN-01/02 / Trungnam2026! (finance)
 *   - 等 13 部门
 *
 * 团队 W2 补:
 *   - SSO 集成 (可选)
 *   - 2FA (可选)
 *   - 多设备 token revoke
 */

import { query, saveDb } from './db';

const PBKDF2_ITER = 310000;
const SALT_BYTES = 16;
const HASH_BITS = 256;

export interface User {
  id: number;
  code: string;
  name: string;
  role: string;
  sub_role?: string;
  department?: string;
}

let _currentUser: User | null = null;

// ============================================================
// 公开 API
// ============================================================

/**
 * 获取当前登录用户 · 业务代码常用
 */
export function getCurrentUser(): User | null {
  if (_currentUser) return _currentUser;
  // 从 localStorage 恢复 (页面刷新后)
  try {
    const raw = localStorage.getItem('mesUser');
    if (raw) {
      _currentUser = JSON.parse(raw) as User;
      return _currentUser;
    }
  } catch {
    /* corrupted · 忽略 */
  }
  return null;
}

/**
 * 登录 · 验证密码 + 设置 currentUser
 */
export async function login(code: string, password: string): Promise<User> {
  const rows = query<{
    id: number;
    code: string;
    name: string;
    role: string;
    sub_role: string | null;
    department: string | null;
    salt: string;
    pwd_hash: string;
  }>(
    `SELECT id, code, name, role, sub_role, department, salt, pwd_hash
     FROM employees
     WHERE code = ? AND COALESCE(deleted_at, '') = ''`,
    [code]
  );

  if (!rows[0]) throw new Error('account_not_found');

  const row = rows[0];
  const valid = await verifyPassword(password, row.salt, row.pwd_hash);
  if (!valid) throw new Error('invalid_password');

  _currentUser = {
    id: row.id,
    code: row.code,
    name: row.name,
    role: row.role,
    sub_role: row.sub_role || undefined,
    department: row.department || undefined,
  };
  localStorage.setItem('mesUser', JSON.stringify(_currentUser));
  document.dispatchEvent(new CustomEvent('tn-user-login', { detail: _currentUser }));
  return _currentUser;
}

/**
 * 登出 · 清 currentUser + localStorage
 */
export function logout(): void {
  _currentUser = null;
  localStorage.removeItem('mesUser');
  document.dispatchEvent(new CustomEvent('tn-user-logout'));
  // 跳登录页 · router.ts 监听 hashchange
  window.location.hash = '#login';
}

/**
 * 改密码 · PBKDF2 hash 写 db
 */
export async function changePassword(code: string, newPassword: string): Promise<void> {
  const { salt, hash } = await hashPassword(newPassword);
  query(`UPDATE employees SET salt = ?, pwd_hash = ? WHERE code = ?`, [salt, hash, code]);
  await saveDb();
}

// ============================================================
// PBKDF2 (R14 严守 · Web Crypto API)
// ============================================================

async function hashPassword(password: string): Promise<{ salt: string; hash: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await deriveKey(password, salt);
  return {
    salt: bytesToHex(salt),
    hash: bytesToHex(hash),
  };
}

async function verifyPassword(password: string, saltHex: string, expectedHashHex: string): Promise<boolean> {
  try {
    const salt = hexToBytes(saltHex);
    const hash = await deriveKey(password, salt);
    return bytesToHex(hash) === expectedHashHex;
  } catch {
    return false;
  }
}

async function deriveKey(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const passwordBytes = enc.encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBytes.buffer.slice(passwordBytes.byteOffset, passwordBytes.byteOffset + passwordBytes.byteLength) as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt.buffer.slice(salt.byteOffset, salt.byteOffset + salt.byteLength) as ArrayBuffer,
      iterations: PBKDF2_ITER,
      hash: 'SHA-256',
    },
    keyMaterial,
    HASH_BITS
  );
  return new Uint8Array(bits);
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return out;
}
